import { Head, useForm } from "@inertiajs/react"
import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
  Select,
  Separator,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes"
import { z } from "zod"

import InputError from "@/components/input-error"
import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { projectDashboardPath, projectNewslettersPath } from "@/routes"
import { EmailSchema, ProjectSchema, ListSchema, SegmentSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  emails: z.array(EmailSchema),
  lists: z.array(ListSchema),
  segments: z.array(SegmentSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function NewsletterNew(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, emails, lists, segments } = validatedProps

  const { data, setData, post, processing, errors } = useForm({
    name: "",
    wants_send_email: true as boolean,
    email_id: "",
    email_list_id: "",
    email_segment_id: "",
    wants_exclude_email_segment: false as boolean,
    wants_send_push: false as boolean,
    push_title: "",
    push_content: "",
    push_list_id: "",
    push_segment_id: "",
    wants_exclude_push_segment: false as boolean,
  })

  const selectedEmailTarget = data.email_segment_id
    ? `segment:${data.email_segment_id}`
    : data.email_list_id
      ? `list:${data.email_list_id}`
      : "all"

  const selectedPushTarget = data.push_segment_id
    ? `segment:${data.push_segment_id}`
    : data.push_list_id
      ? `list:${data.push_list_id}`
      : "all"

  const handleEmailTargetChange = (value: string) => {
    if (value === "all") {
      setData("email_list_id", "")
      setData("email_segment_id", "")
      setData("wants_exclude_email_segment", false)
    } else if (value.startsWith("list:")) {
      setData("email_list_id", value.replace("list:", ""))
      setData("email_segment_id", "")
      setData("wants_exclude_email_segment", false)
    } else if (value.startsWith("segment:")) {
      setData("email_segment_id", value.replace("segment:", ""))
      setData("email_list_id", "")
    }
  }

  const handlePushTargetChange = (value: string) => {
    if (value === "all") {
      setData("push_list_id", "")
      setData("push_segment_id", "")
      setData("wants_exclude_push_segment", false)
    } else if (value.startsWith("list:")) {
      setData("push_list_id", value.replace("list:", ""))
      setData("push_segment_id", "")
      setData("wants_exclude_push_segment", false)
    } else if (value.startsWith("segment:")) {
      setData("push_segment_id", value.replace("segment:", ""))
      setData("push_list_id", "")
    }
  }

  const isEmailConfigured = !data.wants_send_email || data.email_id !== ""
  const isPushConfigured =
    !data.wants_send_push || (data.push_title.trim() !== "" && data.push_content.trim() !== "")
  const hasEnabledChannel = data.wants_send_email || data.wants_send_push
  const canSubmit = hasEnabledChannel && isEmailConfigured && isPushConfigured
  const baseError = (errors as Record<string, string | undefined>).base

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!canSubmit) {
      return
    }

    post(projectNewslettersPath(project.slug))
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Newsletters", href: projectNewslettersPath(project.slug) },
        { title: "New Newsletter" },
      ]}
    >
      <Head title="New Newsletter" />

      <Flex direction="column" gap="6" p="6">
        <Flex direction="column" gap="1">
          <Heading size="6" weight="medium">
            New Newsletter
          </Heading>
          <Text color="gray" size="2">
            Create a new newsletter to send to customers
          </Text>
        </Flex>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <Box>
              <Flex align="center" gap="3" mb="3">
                <Text size="2" weight="bold" color="gray">
                  Basic configuration
                </Text>
                <Separator size="4" style={{ flex: 1 }} />
              </Flex>

              <Card variant="surface" style={{ backgroundColor: "#f8fcff" }}>
                <Flex direction="column" gap="3" p="4">
                  <div>
                    <Text as="div" size="3" weight="bold" mb="2">
                      Name <span className="text-red-500">*</span>
                    </Text>
                    <TextField.Root
                      value={data.name}
                      onChange={(e) => setData("name", e.target.value)}
                      placeholder="Enter newsletter name..."
                      required
                    />
                    <InputError message={errors?.name?.[0]} />
                  </div>
                </Flex>
              </Card>
            </Box>

            <Box>
              <Flex align="center" gap="3" mb="3">
                <Text size="2" weight="bold" color="gray">
                  Email channel
                </Text>
                <Separator size="4" style={{ flex: 1 }} />
              </Flex>

              <Card variant="surface">
                <Flex direction="column" gap="3" p="4">
                  <Flex align="center" justify="between">
                    <Text as="label" size="4" weight="medium">
                      <Flex align="center" gap="2">
                        <Checkbox
                          checked={data.wants_send_email}
                          onCheckedChange={(checked) =>
                            setData("wants_send_email", checked === true)
                          }
                        />
                        Enable email
                      </Flex>
                    </Text>
                    <Badge color={data.wants_send_email ? "green" : "gray"} variant="soft">
                      {data.wants_send_email ? "Enabled" : "Disabled"}
                    </Badge>
                  </Flex>

                  {data.wants_send_email ? (
                    <>
                      <div>
                        <Text as="div" size="3" weight="bold" mb="2">
                          Send to <span className="text-red-500">*</span>
                        </Text>
                        <Select.Root
                          value={selectedEmailTarget}
                          onValueChange={handleEmailTargetChange}
                        >
                          <Select.Trigger placeholder="Select recipients" />
                          <Select.Content position="popper">
                            <Select.Item value="all">All customers</Select.Item>
                            {segments.length > 0 && (
                              <>
                                <Select.Separator />
                                <Select.Group>
                                  <Select.Label>Segments</Select.Label>
                                  {segments.map((segment) => (
                                    <Select.Item key={segment.id} value={`segment:${segment.id}`}>
                                      {segment.name}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </>
                            )}
                            {lists.length > 0 && (
                              <>
                                <Select.Separator />
                                <Select.Group>
                                  <Select.Label>Lists</Select.Label>
                                  {lists.map((list) => (
                                    <Select.Item key={list.id} value={`list:${list.id}`}>
                                      {list.name} ({list.count_customers} customers)
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </>
                            )}
                          </Select.Content>
                        </Select.Root>
                        <InputError
                          message={errors?.email_segment_id?.[0] ?? errors?.email_list_id?.[0]}
                        />
                      </div>

                      {data.email_segment_id && (
                        <Text as="label" size="2" color="gray">
                          <Flex align="center" gap="2">
                            <Checkbox
                              checked={data.wants_exclude_email_segment}
                              onCheckedChange={(checked) =>
                                setData("wants_exclude_email_segment", checked === true)
                              }
                            />
                            Send to everyone except this segment
                          </Flex>
                        </Text>
                      )}

                      <div>
                        <Text as="div" size="3" weight="bold" mb="2">
                          Email template <span className="text-red-500">*</span>
                        </Text>
                        <Select.Root
                          value={data.email_id || undefined}
                          onValueChange={(value) => setData("email_id", value || "")}
                        >
                          <Select.Trigger placeholder="Select an email template" />
                          <Select.Content position="popper">
                            {emails.map((email) => (
                              <Select.Item key={email.id} value={email.id.toString()}>
                                {email.subject ?? "Untitled Email"}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                        {emails.length === 0 && (
                          <Text color="gray" size="1" mt="1">
                            No email templates available. Please create an email template first.
                          </Text>
                        )}
                        <InputError message={errors?.email_id?.[0]} />
                      </div>
                    </>
                  ) : (
                    <Text size="2" color="gray">
                      Enable this channel to choose an email template and target recipients.
                    </Text>
                  )}
                </Flex>
              </Card>
            </Box>

            <Box>
              <Flex align="center" gap="3" mb="3">
                <Text size="2" weight="bold" color="gray">
                  Push channel
                </Text>
                <Separator size="4" style={{ flex: 1 }} />
              </Flex>

              <Card variant="surface" style={{ backgroundColor: "#f8fdf8" }}>
                <Flex direction="column" gap="3" p="4">
                  <Flex align="center" justify="between">
                    <Text as="label" size="4" weight="medium">
                      <Flex align="center" gap="2">
                        <Checkbox
                          checked={data.wants_send_push}
                          onCheckedChange={(checked) =>
                            setData("wants_send_push", checked === true)
                          }
                        />
                        Enable push
                      </Flex>
                    </Text>
                    <Badge color={data.wants_send_push ? "green" : "gray"} variant="soft">
                      {data.wants_send_push ? "Enabled" : "Disabled"}
                    </Badge>
                  </Flex>

                  {data.wants_send_push ? (
                    <>
                      <div>
                        <Text as="div" size="3" weight="bold" mb="2">
                          Send to <span className="text-red-500">*</span>
                        </Text>
                        <Select.Root
                          value={selectedPushTarget}
                          onValueChange={handlePushTargetChange}
                        >
                          <Select.Trigger placeholder="Select recipients" />
                          <Select.Content position="popper">
                            <Select.Item value="all">All customers</Select.Item>
                            {segments.length > 0 && (
                              <>
                                <Select.Separator />
                                <Select.Group>
                                  <Select.Label>Segments</Select.Label>
                                  {segments.map((segment) => (
                                    <Select.Item key={segment.id} value={`segment:${segment.id}`}>
                                      {segment.name}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </>
                            )}
                            {lists.length > 0 && (
                              <>
                                <Select.Separator />
                                <Select.Group>
                                  <Select.Label>Lists</Select.Label>
                                  {lists.map((list) => (
                                    <Select.Item key={list.id} value={`list:${list.id}`}>
                                      {list.name} ({list.count_customers} customers)
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              </>
                            )}
                          </Select.Content>
                        </Select.Root>
                        <InputError
                          message={errors?.push_segment_id?.[0] ?? errors?.push_list_id?.[0]}
                        />
                      </div>

                      {data.push_segment_id && (
                        <Text as="label" size="2" color="gray">
                          <Flex align="center" gap="2">
                            <Checkbox
                              checked={data.wants_exclude_push_segment}
                              onCheckedChange={(checked) =>
                                setData("wants_exclude_push_segment", checked === true)
                              }
                            />
                            Send to everyone except this segment
                          </Flex>
                        </Text>
                      )}

                      <div>
                        <Text as="div" size="3" weight="bold" mb="2">
                          Push title <span className="text-red-500">*</span>
                        </Text>
                        <TextField.Root
                          value={data.push_title}
                          onChange={(e) => setData("push_title", e.target.value)}
                          placeholder="Enter push title..."
                          required
                        />
                        <InputError message={errors?.push_title?.[0]} />
                      </div>

                      <div>
                        <Text as="div" size="3" weight="bold" mb="2">
                          Push content <span className="text-red-500">*</span>
                        </Text>
                        <TextArea
                          value={data.push_content}
                          onChange={(e) => setData("push_content", e.target.value)}
                          placeholder="Enter push content..."
                          required
                          rows={4}
                        />
                        <InputError message={errors?.push_content?.[0]} />
                      </div>
                    </>
                  ) : (
                    <Text size="2" color="gray">
                      Enable this channel to set a push message and recipient target.
                    </Text>
                  )}
                </Flex>
              </Card>
            </Box>

            <InputError message={baseError} />

            <Flex gap="3" justify="end" mt="4">
              <LinkButton href={projectNewslettersPath(project.slug)} variant="soft" color="gray">
                Cancel
              </LinkButton>

              <Button type="submit" disabled={processing || !canSubmit}>
                {processing ? "Creating..." : "Create Newsletter"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </ProjectLayout>
  )
}
